import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DocumentService} from '../../services/document.service';
import {UtilsService} from '../../appSharedModule/services/utils.service';
import {AuthenticationService} from '../../authenticationModule/services/authentication.service';
import {TableService} from '../../services/table.service';
import {Column} from '../../share/models/column';
import * as _ from 'lodash';
import {DownloadLogFilterCriteria} from '../../models/filter/download-log-filter-criteria';
import {SelectItem} from 'primeng/api';
import {DocumentDownloadLog} from '../../models/DocumentDownloadLog';
import {BaseClientComponent} from '../base-client.component';

@Component({
  selector: 'app-download-log',
  templateUrl: './download-log.component.html',
  styleUrls: ['./download-log.component.scss']
})
export class DownloadLogComponent extends BaseClientComponent implements OnInit {

  cols: Column[];
  downloadsList: DocumentDownloadLog[];
  filteredDownloadsList: DocumentDownloadLog[];
  records: number;
  firstPage = 0;
  downloadLogFilterCriteria: DownloadLogFilterCriteria;
  userChoicesFiltersDefaultDownloadLog = {
    documentNameOrTitle: '',
    companyName: '',
    fullNameOrEmail: '',
    dateFrom: null,
    dateTo: null,
  };
  companyList: SelectItem[] = [];
  userChoicesActiveFilters = _.clone(this.userChoicesFiltersDefaultDownloadLog);

  constructor(private _route: ActivatedRoute,
              public tableService: TableService,
              private authenticationService: AuthenticationService,
              private documentService: DocumentService,
              private utilsService: UtilsService) {
    super();
  }

  ngOnInit() {
    this.initializeColumns();
    this._route.queryParams.subscribe(params => {
      this.userChoicesActiveFilters = this.utilsService.loadUserPref('downloadLogFilter', this.userChoicesFiltersDefaultDownloadLog);
      const titleGetParameter = (params['title']) ? params['title'] : '';
      this.downloadLogFilterCriteria = new DownloadLogFilterCriteria((titleGetParameter !== '') ? titleGetParameter : this.userChoicesActiveFilters.documentNameOrTitle,
        this.userChoicesActiveFilters.companyName, this.userChoicesActiveFilters.fullNameOrEmail, this.userChoicesActiveFilters.dateTo, this.userChoicesActiveFilters.dateFrom);
    });
    this._route.paramMap.subscribe(params => {
      const currentCompanyUniqId = params.get('currentCompanyUniqId');
      if (currentCompanyUniqId !== this.authenticationService.getUserCurrent().company.uniqid) {
        if (this.authenticationService.getUserCurrent().company.uniqid === 'admin') {
          location.assign('/#/mckay/' + this.authenticationService.getUserCurrent().company.uniqid);
        } else {
          location.assign('/#/client/' + this.authenticationService.getUserCurrent().company.uniqid);
        }
      }
    });
    const response$ = this.authenticationService.waitForAuthenticationThenLaunch(() => {
      this.getDownloadedDocuments();
    });
    this.subscriptions.push(response$);
  }

  redirectionToNotFound() {
    location.assign('/#/404');
  }

  filter() {
    if (this.downloadsList) {
      this.utilsService.saveUserData('downloadLogFilter',
        {
          'title': this.downloadLogFilterCriteria.documentNameOrTitle,
          'companyName': this.downloadLogFilterCriteria.companyName,
          'userName': this.downloadLogFilterCriteria.fullNameOrEmail,
          'dateFrom': this.downloadLogFilterCriteria.dateFrom,
          'dateTo': this.downloadLogFilterCriteria.dateFrom
        });
      this.filteredDownloadsList = [];
      this.downloadsList.forEach(document => {
        if (this.hasDocumentNameOrTitle(document, this.downloadLogFilterCriteria.documentNameOrTitle)
          && this.hasCompanyName(document, this.downloadLogFilterCriteria.companyName)
          && this.hasFullNameOrEmail(document, this.downloadLogFilterCriteria.fullNameOrEmail)) {
          this.filteredDownloadsList.push(document);
        }
      });
    }
    this.resetDataWithFromDate();
    this.resetDataWithToDate();
    this.records = this.filteredDownloadsList ? this.filteredDownloadsList.length : 0;
  }

  resetDataWithFromDate() {
    if (!this.downloadLogFilterCriteria.dateFrom) {
      return;
    }
    this.filteredDownloadsList =
      this.filteredDownloadsList.filter(key => new Date(key.downloadDate) >= new Date(this.downloadLogFilterCriteria.dateFrom));

  }

  resetDataWithToDate() {
    if (!this.downloadLogFilterCriteria.dateTo) {
      return;
    }
    this.filteredDownloadsList =
      this.filteredDownloadsList.filter(key => {
        const downloadDate = new Date(key.downloadDate);
        const dateTo = new Date(this.downloadLogFilterCriteria.dateTo);
        downloadDate.setHours(0, 0, 0, 0);
        dateTo.setHours(0, 0, 0, 0);
        return downloadDate <= dateTo;
      });
  }

  hasDocumentNameOrTitle(document, documentNameOrTitleFilter) {
    if (!documentNameOrTitleFilter) {
      return true;
    }
    return document.documentTitle.toLowerCase().includes(documentNameOrTitleFilter.toLowerCase())
      || document.documentName.toLowerCase().includes(documentNameOrTitleFilter.toLowerCase());
  }

  hasCompanyName(document, companyNameFilter) {
    if (!companyNameFilter) {
      return true;
    }
    if (!document.userCompanyName) {
      return false;
    }
    return document.userCompanyName.toLowerCase().includes(companyNameFilter.toLowerCase());
  }

  hasFullNameOrEmail(document, fullNameOrEmailFilter) {
    if (!fullNameOrEmailFilter) {
      return true;
    }
    if (!document.userName || !document.userEmail) {
      return false;
    }
    return document.userName.toLowerCase().includes(fullNameOrEmailFilter.toLowerCase())
      || document.userEmail.toLowerCase().includes(fullNameOrEmailFilter.toLowerCase());
  }

  resetTable(): void {
    this.downloadLogFilterCriteria.documentNameOrTitle = null;
    this.downloadLogFilterCriteria.companyName = null;
    this.downloadLogFilterCriteria.fullNameOrEmail = null;
    this.downloadLogFilterCriteria.dateFrom = null;
    this.downloadLogFilterCriteria.dateTo = null;
    this.firstPage = 0;
    this.filter();
  }

  getFilename() {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    return 'download_log_' + year + month + day + '_' + now.getHours() + now.getMinutes();
  }

  getUserName(document) {
    if (!document.userName) {
      document.userName = 'User was removed';
    }
    return document.userName;
  }

  getUserCompanyName(document) {
    return (!document.userCompanyName) ? '' : document.userCompanyName;
  }

  getUserEmail(document) {
    return (!document.userEmail) ? '' : document.userEmail;
  }

  setCompaniesList(downloadsList: DocumentDownloadLog[]) {
    this.companyList = downloadsList.map(download => ({
      label: download.userCompanyName,
      value: download.userCompanyName
    } as SelectItem));
    // Remove duplicates.
    this.companyList = this.companyList.filter(
      (item, index, self) => item.value !== '' && item.label !== '' && self.findIndex(i => i.value === item.value) === index
    );
    // Order list
    this.companyList.sort((selectItem1, selectItem2) => {
      if (selectItem1.label < selectItem2.label) {
        return -1;
      }
      if (selectItem1.label > selectItem2.label) {
        return 1;
      }
      return 0;
    });
    this.companyList.unshift({label: 'ALL', value: null});
  }

  private initializeColumns() {
    this.cols = [
      {field: 'downloadDate', header: 'Download Date', sortable: false},
      {field: 'documentName', header: 'Document Name', sortable: true},
      {field: 'documentTitle', header: 'Document Title', sortable: true},
      {field: 'userName', header: 'User Name', sortable: true},
      {field: 'userCompanyName', header: 'Company Name', sortable: true},
      {field: 'userEmail', header: 'Email', sortable: true}
    ];
  }

  private getDownloadedDocuments() {
    this.utilsService.lockUi();
    this.documentService.getDownloadedDocuments().subscribe({
        next: documents => {
          this.downloadsList = documents.sort((a, b) => {
            return <any>new Date(b.downloadDate) - <any>new Date(a.downloadDate);
          });
          this.filteredDownloadsList = [];
          this.filteredDownloadsList.push(...this.downloadsList);
          this.records = this.filteredDownloadsList ? this.filteredDownloadsList.length : 0;
          if (this.downloadLogFilterCriteria.documentNameOrTitle || this.downloadLogFilterCriteria.companyName !== null || this.downloadLogFilterCriteria.fullNameOrEmail) {
            this.filter();
          }
          this.utilsService.unlockUi();
          this.setCompaniesList(this.downloadsList);
        },
        error: () => {
          this.redirectionToNotFound();
        }
      }
    );
  }
}
